import { Link as RouterLink } from "@tanstack/react-router";
import type { AnchorHTMLAttributes, ReactNode } from "react";

type Preload = "intent" | "viewport" | "render" | false;

export interface LinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href?: string;
  /**
   * TanStack Router preload strategy. Defaults to `"intent"` (prefetch on
   * hover/focus). Pass `false` to disable (e.g. variant swatches).
   */
  preload?: Preload;
  /**
   * Delay in ms before an `"intent"` preload fires. Defaults to 250ms so that
   * merely sweeping the cursor across a shelf does not trigger a prefetch per
   * card. `createDecoRouter` does not expose `defaultPreloadDelay`, so we set
   * it per-link here.
   */
  preloadDelay?: number;
  children?: ReactNode;
}

/**
 * Internal path = starts with a single "/" (not "//", which is a
 * protocol-relative external URL). Mirrors the framework's own
 * `serverFnPagePath` check.
 */
const isInternal = (href?: string): href is string =>
  !!href && href.charCodeAt(0) === 47 /* "/" */ && href.charCodeAt(1) !== 47;

/**
 * Drop-in replacement for `<a>` that renders a TanStack Router `<Link>` for
 * internal paths (SPA navigation + prefetch) and falls back to a plain `<a>`
 * for external / hash / mailto / tel / target / download links.
 *
 * The rendered SPA anchor does NOT carry `data-spa`, so the global hover
 * HTML-prefetch script (see `src/sdk/htmlPrefetch.ts`) also fires
 * `<link rel="prefetch" as="document">` on hover — warming the edge-cached
 * HTML. The router's `preload="intent"` handles data prefetch in parallel.
 */
export default function Link({
  href,
  preload = "intent",
  preloadDelay = 250,
  target,
  download,
  children,
  ...rest
}: LinkProps) {
  const external = target && target !== "_self";

  if (!isInternal(href) || external || download != null) {
    return (
      <a href={href} target={target} download={download} {...rest}>
        {children}
      </a>
    );
  }

  const [path, qs] = href.split("?");
  const search = qs
    ? Object.fromEntries(new URLSearchParams(qs))
    : undefined;

  return (
    <RouterLink
      // Catch-all `/$` route resolves CMS pages from arbitrary paths; the
      // typed Link API can't know these at compile time, so the cast is
      // contained here and never leaks to call sites.
      to={path as never}
      search={search as never}
      preload={preload}
      preloadDelay={preloadDelay}
      {...(rest as Record<string, unknown>)}
    >
      {children}
    </RouterLink>
  );
}
